import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Landmark, WalletCards } from 'lucide-react';
import { toast } from 'sonner';
import { BellIcon } from '../../components/BellIcon';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const cardStyle = {
  ...hostCardStyle,
  width: '100%',
  maxWidth: 360,
  padding: '18px 16px 20px',
};

const EuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 0C12.76 0 15.26 1.12 17.07 2.93C18.88 4.74 20 7.24 20 10C20 12.76 18.88 15.26 17.07 17.07C15.26 18.88 12.76 20 10 20C7.24 20 4.74 18.88 2.93 17.07C1.12 15.26 0 12.76 0 10C0 7.24 1.12 4.74 2.93 2.93C4.74 1.12 7.24 0 10 0Z" fill="white"/>
    <path d="M10 0.846C15.056 0.846 19.154 4.944 19.154 10C19.154 15.056 15.056 19.154 10 19.154C4.944 19.154 0.846 15.056 0.846 10C0.846 4.944 4.944 0.846 10 0.846Z" fill="#003399"/>
    <path d="M9.35 17.61L10 17.14L10.65 17.61L10.4 16.84L11.05 16.37H10.25L10 15.61L9.75 16.37H8.95L9.6 16.84L9.35 17.61ZM10 2.39L9.75 3.16H8.95L9.6 3.63L9.35 4.39L10 3.92L10.65 4.39L10.4 3.63L11.05 3.16H10.25L10 2.39ZM14.35 15.49L13.7 15.96L13.95 16.72L13.3 16.25L12.66 16.72L12.9 15.96L12.26 15.49H13.06L13.3 14.73L13.55 15.49H14.35ZM16.77 13.07L16.12 13.54L16.37 14.3L15.72 13.83L15.08 14.3L15.32 13.54L14.68 13.07H15.48L15.72 12.31L15.97 13.07H16.77ZM15.72 5.7L15.97 6.46H16.77L16.12 6.93L16.37 7.69L15.72 7.22L15.08 7.69L15.32 6.93L14.68 6.46H15.48L15.72 5.7ZM12.26 4.04H13.06L13.3 3.28L13.55 4.04H14.35L13.7 4.51L13.95 5.27L13.3 4.8L12.66 5.27L12.9 4.51L12.26 4.04ZM17.26 11L16.61 10.53L15.96 11L16.21 10.24L15.56 9.76H16.36L16.61 9L16.86 9.76H17.66L17.01 10.24L17.26 11ZM5.65 15.49L6.29 15.96L6.05 16.72L6.7 16.25L7.34 16.72L7.1 15.96L7.74 15.49H6.94L6.7 14.73L6.45 15.49H5.65ZM3.23 13.07L3.88 13.54L3.63 14.3L4.28 13.83L4.92 14.3L4.68 13.54L5.32 13.07H4.52L4.28 12.31L4.03 13.07H3.23ZM4.28 5.7L4.03 6.46H3.23L3.88 6.93L3.63 7.69L4.28 7.22L4.92 7.69L4.68 6.93L5.32 6.46H4.52L4.28 5.7ZM7.74 4.04H6.94L6.7 3.28L6.45 4.04H5.65L6.3 4.51L6.05 5.27L6.7 4.8L7.34 5.27L7.1 4.51L7.74 4.04ZM2.74 11L3.39 10.53L4.04 11L3.79 10.24L4.44 9.76H3.64L3.39 9L3.14 9.76H2.34L2.99 10.24L2.74 11Z" fill="#FFCC02"/>
  </svg>
);

const DoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="10.4" fill="#BEF9D8" stroke="#42A246" strokeWidth="1.2"/>
    <path d="M6.8 10.7C7.7 11.2 8.3 11.7 9 12.5C10.5 10.1 12.2 8.8 14.2 7" stroke="#42A246" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="30" height="34" viewBox="0 0 23 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M11.46 0C15.75 2.72 19.62 4 22.95 3.7C23.53 15.45 19.19 22.38 11.51 25.28C4.08 22.57 -0.31 15.93 0.02 3.52C3.92 3.73 7.75 2.88 11.46 0Z" fill="#51B53C"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M11.46 1.45C15.26 3.86 18.69 4.99 21.63 4.72C22.15 15.12 18.31 21.26 11.5 23.82C4.93 21.43 1.05 15.55 1.34 4.57C4.79 4.75 8.18 4 11.46 1.45Z" fill="white"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M11.57 4.02C14.49 5.87 17.14 6.75 19.4 6.54C19.8 14.55 16.84 19.28 11.6 21.26C6.16 19.28 3.2 14.55 3.6 6.54C5.86 6.75 8.51 5.87 11.43 4.02L11.5 4.07L11.57 4.02Z" fill="#51B53C"/>
    <path d="M7.4 12.1L10.2 14.5L15.2 8.9" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function HostPayoutPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [bankDetails, setBankDetails] = useState(null);
  const [history, setHistory] = useState(null);
  const [iban, setIban] = useState('');
  const [holderName, setHolderName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPayoutDashboard = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/host/payouts/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHistory(response.data);
        setBankDetails(response.data?.bank_details || null);
      } catch (error) {
        console.info('No payout dashboard found yet.', error?.response?.data || error?.message || error);
      } finally {
        setLoading(false);
      }
    };

    fetchPayoutDashboard();
  }, []);

  const balances = history?.balances || {};
  const latestSummary = useMemo(() => (history?.monthly_summaries || [])[0] || null, [history]);

  const handleLinkBankAccount = async () => {
    if (!iban.trim() || !holderName.trim()) {
      toast.error('Please fill in all bank account fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const normalizedIban = iban.replace(/\s+/g, '').toUpperCase();
      const res = await axios.post(
        `${API}/host/bank-details`,
        { iban: normalizedIban, holder_name: holderName.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBankDetails({ holder_name: holderName.trim(), iban_masked: `****${normalizedIban.slice(-4)}`, currency: 'EUR' });
      setHistory((current) => ({ ...current, bank_details: { holder_name: holderName.trim(), iban_masked: `****${normalizedIban.slice(-4)}`, currency: 'EUR' } }));
      setIban('');
      setHolderName('');
      if (res.data?.auto_payout_triggered) {
        toast.success(`Bank account linked — your pending payout of €${Number(res.data.auto_payout_amount || 0).toFixed(2)} is on its way`);
      } else {
        toast.success('Bank account linked');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link bank account');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBankAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/host/bank-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBankDetails(null);
      setHistory((current) => ({ ...current, bank_details: null }));
      toast.success('Bank account removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove bank account');
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className="app-shell" data-testid="host-payout-page" style={{ backgroundColor: 'var(--app-bg)', minHeight: '100vh', paddingBottom: 32 }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button data-testid="host-payout-back-button" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={34} color="var(--text)" strokeWidth={1.8} />
        </button>
        <button data-testid="host-payout-bell-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <BellIcon />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 16px' }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 148, height: 130, objectFit: 'contain', marginTop: 8 }} />
        <h1 style={{ fontFamily: font, fontSize: 32, fontWeight: 700, color: 'var(--text)', margin: '14px 0 18px' }}>Payout</h1>

        <div style={{ width: '100%', maxWidth: 360, display: 'grid', gap: 12 }}>
          <div data-testid="host-payout-balance-card" style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 50, height: 50, borderRadius: 16, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WalletCards size={24} color="var(--accent-purple)" />
              </div>
              <div>
                <p style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Manual monthly payouts</p>
                <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: '4px 0 0' }}>We lock each earning in a ledger, then pay it manually by month.</p>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Pending balance</span>
                <strong data-testid="host-payout-pending-balance" style={{ fontFamily: font, fontSize: 20 }}>{Number(balances.pending_total || 0).toFixed(2)}€</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Generated batch</span>
                <strong data-testid="host-payout-generated-balance" style={{ fontFamily: font, fontSize: 18 }}>{Number(balances.generated_total || 0).toFixed(2)}€</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>Already paid</span>
                <strong data-testid="host-payout-paid-balance" style={{ fontFamily: font, fontSize: 18 }}>{Number(balances.paid_total || 0).toFixed(2)}€</strong>
              </div>
            </div>
          </div>

          {latestSummary ? (
            <div data-testid="host-payout-latest-summary-card" style={cardStyle}>
              <p style={{ fontFamily: font, fontSize: 22, fontWeight: 700, margin: 0, color: 'var(--text)' }}>Latest batch</p>
              <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text)', margin: 0 }}>{latestSummary.payout_month} · Batch {latestSummary.batch_sequence}</p>
              <p data-testid="host-payout-latest-summary-amount" style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--status-success-text)', margin: 0 }}>{Number(latestSummary.total_amount || 0).toFixed(2)}€</p>
              <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', margin: 0 }}>Status: {latestSummary.status}</p>
            </div>
          ) : null}
        </div>

        <div data-testid="host-payout-bank-card" style={cardStyle}>
          {!bankDetails ? (
            <>
              <p style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0, textAlign: 'center', lineHeight: 1.05 }}>
                No payout method connected
              </p>
              <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text)', margin: '12px 0 18px', textAlign: 'center', lineHeight: 1.05 }}>
                Add your IBAN so manual monthly host payouts can be sent without mistakes.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <EuIcon />
                <span style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>IBAN</span>
              </div>
              <input
                data-testid="host-payout-iban-input"
                value={iban}
                onChange={(event) => setIban(event.target.value.toUpperCase())}
                placeholder="Enter your IBAN"
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 14, border: '1px solid rgba(112, 63, 246, 0.14)', background: 'var(--surface)', color: 'var(--text)', fontFamily: font, fontSize: 18, textAlign: 'center', marginBottom: 10 }}
              />
              <input
                data-testid="host-payout-holder-input"
                value={holderName}
                onChange={(event) => setHolderName(event.target.value)}
                placeholder="Account holder’s name"
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 14, border: '1px solid rgba(112, 63, 246, 0.14)', background: 'var(--surface)', color: 'var(--text)', fontFamily: font, fontSize: 18, textAlign: 'center', marginBottom: 16 }}
              />
              <button
                type="button"
                data-testid="host-payout-link-bank-button"
                onClick={handleLinkBankAccount}
                disabled={saving}
                style={{ width: '100%', border: 'none', borderRadius: 14, padding: '14px 18px', background: '#E53935', color: '#FFFFFF', fontFamily: font, fontSize: 22, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Linking...' : 'Link Bank Account'}
              </button>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <Landmark size={42} color="var(--text-2)" strokeWidth={1.7} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1 }}>Bank Account</p>
                  <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '2px 0 0', lineHeight: 1.05 }}>
                    {bankDetails.holder_name}, {bankDetails.iban_masked}
                  </p>
                  <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text)', margin: '2px 0 0' }}>({bankDetails.currency})</p>
                </div>
              </div>
              <button
                type="button"
                data-testid="host-payout-remove-bank-button"
                onClick={handleRemoveBankAccount}
                style={{ background: 'none', border: 'none', color: '#E53935', textDecoration: 'underline', fontFamily: font, fontSize: 22, cursor: 'pointer', alignSelf: 'center', padding: 0 }}
              >
                Remove
              </button>
            </div>
          )}
        </div>

        <div style={{ width: '100%', maxWidth: 360, marginTop: 16 }}>
          <div data-testid="host-payout-direct-info" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 4px' }}>
            <DoneIcon />
            <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text)', margin: 0, lineHeight: 1.05 }}>
              Every host earning is saved transaction-by-transaction before payout month closes.
            </p>
          </div>

          <div data-testid="host-payout-viva-card" style={{ width: '100%', boxSizing: 'border-box', borderRadius: 18, border: '1px solid rgba(112, 63, 246, 0.14)', background: 'var(--surface)', padding: '8px 10px', marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <ShieldIcon />
              <div>
                <p style={{ fontFamily: font, fontSize: 15, fontWeight: 400, color: 'var(--text)', margin: 0, lineHeight: 1.01 }}>Your payout ledger is saved securely.</p>
                <p style={{ fontFamily: font, fontSize: 15, fontWeight: 400, color: 'var(--text)', margin: '2px 0 0', lineHeight: 1.01 }}>Manual monthly transfers are tracked end-to-end.</p>
              </div>
            </div>
          </div>

          <button
            type="button"
            data-testid="host-payout-history-link"
            onClick={() => navigate('/host/settings/payouts/history')}
            style={{ ...cardStyle, marginTop: 16, border: '1px solid var(--card-border)', color: 'var(--text)', fontFamily: font, fontSize: 28, fontWeight: 700, textAlign: 'center', cursor: 'pointer' }}
          >
            Payout History →
          </button>
        </div>

        <div style={{ marginTop: 28, display: 'flex', gap: 14, justifyContent: 'center', borderTop: '1px solid var(--hairline)', width: '100%', maxWidth: 360, paddingTop: 18 }}>
          <a href="/terms" data-testid="host-payout-terms-service" style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', textDecoration: 'underline' }}>Terms of Service</a>
          <span style={{ color: 'var(--text-3)' }}>•</span>
          <a href="/terms" data-testid="host-payout-payments-terms" style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', textDecoration: 'underline' }}>Payments Terms</a>
        </div>
      </div>
    </div>
  );
}